import { moNum } from "@/lib/moNum";
import { LEVELS, ROWS, POS, locId, fmt, breakdown } from "./shelfShared";

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export const printHtml = (html) => {
  // Chrome names "Save as PDF" files after the PARENT page title, not the iframe's.
  const docTitle = (html.match(/<title>([^<]*)<\/title>/i) || [])[1] || "";
  const prevTitle = document.title;
  if (docTitle) document.title = docTitle;
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } finally {
      setTimeout(() => { iframe.remove(); document.title = prevTitle; }, 1000);
    }
  };
  iframe.srcdoc = html;
  document.body.appendChild(iframe);
};

export const buildPullPlanHtml = (pullPlan) => {
  const rows = pullPlan.components.map((c) => {
    const head = esc(`${c.name}${c.type ? ` · ${c.type}` : ""}`);
    const locs = c.locations.map((l) => `<tr><td>${head}</td><td class="mono">${esc(l.location)}</td><td>${breakdown(l.units, l.units_per_case, l.units_per_pallet).join(" · ")}</td></tr>`).join("");
    const un = c.unassigned_needed > 0 ? `<tr><td>${head}</td><td class="mono">—</td><td class="warn">+ ${breakdown(c.unassigned_needed, c.units_per_case, c.units_per_pallet).join(" · ")} not on a shelf (Unassigned)</td></tr>` : "";
    const none = c.locations.length === 0 && c.unassigned_needed <= 0 ? `<tr><td>${head}</td><td>—</td><td>—</td></tr>` : "";
    return locs + none + un;
  }).join("");
  return `<html><head><title>Pull List ${esc(moNum(pullPlan.mwo_number))}</title><style>
    body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}
    h1{font-size:18px;margin:0} .sub{color:#475569;font-size:12px;margin:4px 0 16px}
    table{width:100%;border-collapse:collapse;font-size:13px}
    th,td{border:1px solid #cbd5e1;padding:6px 8px;text-align:left}
    th{background:#002FA7;color:#fff;font-size:11px;text-transform:uppercase}
    .mono{font-family:monospace;font-weight:bold;color:#002FA7} .warn{color:#b45309}
  </style></head><body>
    <h1>Production Pull List — ${esc(moNum(pullPlan.mwo_number))}</h1>
    <div class="sub">${esc(pullPlan.product_name || "")}${pullPlan.design_name ? ` · ${esc(pullPlan.design_name)}` : ""} · Remaining to produce: ${fmt(pullPlan.remaining_bottles)} bottles · Printed ${new Date().toLocaleString()}</div>
    <table><thead><tr><th style="width:40px"></th><th>Component</th><th>Location</th><th>Pull Qty</th></tr></thead>
    <tbody>${rows.replace(/<tr>/g, '<tr><td style="width:40px">&#9744;</td>')}</tbody></table>
  </body></html>`;
};

export const buildShelvesHtml = (racks, variants) => {
  const slotHtml = (rack, loc) => {
    const s = (rack.slots || {})[loc];
    if (!s) return `<div class="cell empty"><span class="loc">${loc}</span><span class="mt">Empty</span></div>`;
    const qty = breakdown(s.units, s.units_per_case, s.units_per_pallet).join("<br/>");
    const sub = esc(`${s.type_name || ""}${s.design_name ? ` · ${s.design_name}` : ""}`);
    return `<div class="cell full"><span class="loc">${loc}</span><div class="nm">${esc(s.component_name || "")}</div><div class="sub">${sub}</div><div class="qty">${qty}</div></div>`;
  };
  const rackHtml = racks.map((rack) => {
    const filled = Object.keys(rack.slots || {}).length;
    const levels = LEVELS.map((level) => {
      const cols = ROWS.map((row) => {
        const cells = [...POS].reverse().map((p) => slotHtml(rack, locId(rack.code, level, row, p))).join("");
        return `<div class="rowcol"><div class="rowlbl">Row ${row}</div>${cells}</div>`;
      }).join("");
      return `<div class="level"><div class="levellbl">Level ${level}</div><div class="rowwrap">${cols}</div></div>`;
    }).join("");
    return `<div class="rack"><div class="rackhdr">${esc(rack.name)} <span class="fill">${filled}/32 filled</span></div>${levels}</div>`;
  }).join("");

  const totals = variants
    .map((v) => ({ ...v }))
    .filter((v) => v.stock > 0.0001)
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((v) => `<tr><td>${esc(v.name)}</td><td>${esc(v.sub)}</td><td>${breakdown(v.stock, v.upc, v.upp).join(" · ")}</td></tr>`)
    .join("");

  return `<html><head><title>Shelf View</title><style>
    @page{size:11in 17in landscape;margin:0.3in}
    *{box-sizing:border-box}
    body{font-family:Arial,sans-serif;margin:0;color:#0f172a}
    h1{font-size:16px;margin:0} .sub{color:#475569;font-size:10px;margin:2px 0 6px}
    h2{font-size:12px;margin:2px 0 5px;border-bottom:2px solid #002FA7;padding-bottom:3px}
    .right{float:right;width:2.9in;margin:0 0 6px 10px}
    table{width:100%;border-collapse:collapse;font-size:9px}
    th,td{border:1px solid #cbd5e1;padding:3px 5px;text-align:left}
    th{background:#002FA7;color:#fff;font-size:8px;text-transform:uppercase}
    .racks{font-size:0}
    .rack{display:inline-block;vertical-align:top;border:1px solid #cbd5e1;padding:3px;width:1.55in;margin:0 4px 4px 0;page-break-inside:avoid}
    .rackhdr{font-weight:bold;font-size:9px;background:#f1f5f9;padding:2px 4px;margin-bottom:3px;display:flex;justify-content:space-between}
    .fill{font-weight:normal;color:#64748b;font-size:8px}
    .level{border:1px solid #e2e8f0;margin-bottom:3px}
    .levellbl{background:#1e293b;color:#fff;font-size:8px;font-weight:bold;text-transform:uppercase;padding:1px 4px}
    .rowwrap{display:flex;gap:3px;padding:3px}
    .rowcol{flex:1;min-width:0} .rowlbl{text-align:center;font-size:7px;color:#64748b;font-weight:600;margin-bottom:1px}
    .cell{border:1px solid #cbd5e1;padding:2px;margin-bottom:2px;height:44px;font-size:7px;overflow:hidden}
    .cell.empty{border-style:dashed;color:#94a3b8;text-align:center}
    .cell.full{background:#eef2ff;border-color:#002FA7}
    .loc{font-size:6px;color:#94a3b8;text-transform:uppercase;font-weight:700;display:block;line-height:1.1}
    .nm{font-weight:bold;font-size:8px;line-height:1.05} .cell .sub{color:#64748b;font-size:6px;margin:0;line-height:1.05}
    .qty{color:#334155;font-size:6px;margin-top:1px;line-height:1.1}
  </style></head><body>
    <h1>Warehouse Shelf View</h1>
    <div class="sub">Printed ${new Date().toLocaleString()} · Tabloid 11×17 landscape</div>
    <div class="right">
      <h2>Total In Stock</h2>
      <table><thead><tr><th>Component</th><th>Variant</th><th>Qty</th></tr></thead>
      <tbody>${totals || '<tr><td colspan="3">No stock recorded.</td></tr>'}</tbody></table>
    </div>
    <h2>Rack Layout</h2>
    <div class="racks">${rackHtml}</div>
  </body></html>`;
};
